'use client';

import React, { FC, type JSX, useLayoutEffect, useRef } from 'react';
import { useAccount } from 'wagmi';

import { ALLOW_GIFT, ORIGINAL_URL } from '@/global/constants/conmmon.constant';
import { RewardType } from '@/global/enums/common.enums';
import { usePathname, useRouter } from '@/i18n/routing';
import { useAppSelector } from '@/store/utils';

interface WithAuthProps {
  LoadingComponent?: React.ComponentType;
}

export function withAuth<Props>(Component: FC<Props>, { LoadingComponent }: WithAuthProps = {}) {
  const ComponentWithAuth: FC<Props> = props => {
    const router = useRouter();
    const pathname = usePathname();
    const { nodeId } = useAppSelector(state => state.app);
    const { projectInfo } = useAppSelector(state => state.project);
    const { address, status } = useAccount();
    const prevStatus = useRef(status);

    const isLocalEnvironment =
      process.env.NEXT_PUBLIC_IS_LOCAL === 'true' || ORIGINAL_URL.includes(window.location.hostname);

    useLayoutEffect(() => {
      if (!nodeId) return;
      const homePath = isLocalEnvironment ? `/${nodeId}` : `/`;
      const giftPath = isLocalEnvironment ? `/${nodeId}/claim` : `/claim`;
      const redemptionPath = isLocalEnvironment ? `/${nodeId}/redemption` : `/redemption`;

      if (
        (prevStatus.current === 'connecting' && status === 'disconnected') ||
        (prevStatus.current === 'connected' && status === 'disconnected')
      ) {
        return router.push(homePath);
      }
      prevStatus.current = status;
      if (projectInfo && projectInfo.referral.rewardType !== RewardType.NFT_NODE && pathname === redemptionPath) {
        return router.push(homePath);
      }

      if (!ALLOW_GIFT.includes(nodeId) && pathname === giftPath) {
        return router.push(homePath);
      }
    }, [router, prevStatus.current, status, isLocalEnvironment, nodeId, projectInfo, pathname]);

    if (address) {
      return <Component {...(props as JSX.IntrinsicAttributes & Props)} />;
    }

    return LoadingComponent ? <LoadingComponent /> : null;
  };

  ComponentWithAuth.displayName = `withAuth(${Component.displayName || Component.name})`;

  return ComponentWithAuth;
}
